import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../utils/api/axiosInstance";
import SimparyChat from "../wizard/SimparyChat";
import {
  buildIdentityInitialMessage,
  buildSuggestedPrompts,
} from "../../utils/identityOnboardingUtils";
import styles from "../../styles/components/onboarding/IdentityOnboardingChat.module.css";

const IdentityOnboardingChat = ({
  displayName = null,
  prefillPrompt = null,
  onPrefillConsumed = null,
  onComplete = null,
  skipProfileFetch = false,
  initialCompleteness = null,
}) => {
  const chatRef = useRef(null);
  const [completeness, setCompleteness] = useState(initialCompleteness);
  const [suggestedPrompts, setSuggestedPrompts] = useState(() =>
    buildSuggestedPrompts(initialCompleteness?.missing || []),
  );
  const [prefill, setPrefill] = useState(prefillPrompt);

  useEffect(() => {
    setPrefill(prefillPrompt);
  }, [prefillPrompt]);

  useEffect(() => {
    if (skipProfileFetch) {
      setCompleteness(initialCompleteness);
      setSuggestedPrompts(
        buildSuggestedPrompts(initialCompleteness?.missing || []),
      );
      return undefined;
    }

    let cancelled = false;
    api
      .get("/users/me/onboarding/shopping-profile")
      .then((res) => {
        if (cancelled) return;
        const nextCompleteness = res.data?.completeness || null;
        setCompleteness(nextCompleteness);
        setSuggestedPrompts(buildSuggestedPrompts(nextCompleteness?.missing || []));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [skipProfileFetch, initialCompleteness]);

  const initialMessage = useMemo(
    () => buildIdentityInitialMessage(displayName, completeness),
    [displayName, completeness],
  );

  const handleIdentityCompletenessChange = useCallback((next) => {
    if (!next) return;
    setCompleteness(next);
    setSuggestedPrompts(buildSuggestedPrompts(next.missing || []));
  }, []);

  const handleSuggestedPromptsFromApi = useCallback((prompts) => {
    if (Array.isArray(prompts) && prompts.length > 0) {
      setSuggestedPrompts(prompts);
    }
  }, []);

  const handleIdentityAccepted = useCallback(
    (data) => {
      if (data?.completeness) {
        setCompleteness(data.completeness);
        setSuggestedPrompts(buildSuggestedPrompts(data.completeness.missing || []));
      }
      if (data?.completeness?.isReady) {
        onComplete?.(data);
      }
    },
    [onComplete],
  );

  const handleChipClick = (prompt) => {
    chatRef.current?.sendPrompt(prompt);
  };

  const progressPercent = completeness?.percent ?? 0;

  return (
    <div>
      {completeness && !completeness.isReady && (
        <div className={styles.progressWrap}>
          <div className={styles.progressMeta}>
            <span>Profile completeness</span>
            <span>{progressPercent}%</span>
          </div>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Profile completeness"
          >
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {suggestedPrompts.length > 0 && (
        <div className={styles.promptChips}>
          {suggestedPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className={styles.promptChip}
              onClick={() => handleChipClick(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      <div className={styles.chatShell}>
        <SimparyChat
          ref={chatRef}
          embedded
          panel
          mode="identity"
          initialMessage={initialMessage}
          prefillPrompt={prefill}
          onPrefillConsumed={onPrefillConsumed}
          hideIdentityBanner
          onIdentityCompletenessChange={handleIdentityCompletenessChange}
          onSuggestedPromptsChange={handleSuggestedPromptsFromApi}
          onIdentityAccepted={handleIdentityAccepted}
        />
      </div>
    </div>
  );
};

export default IdentityOnboardingChat;
